import { Link } from 'react-router-dom'
import { ArrowRight, MapPin, ChevronLeft, ChevronRight } from 'lucide-react'
import AnimatedSection from '@/components/AnimatedSection'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { useProjects } from '@/hooks/useProjects'

export default function ProjectsShowcase() {
  const { projects, isLoading } = useProjects()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    slidesToScroll: 1,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  // Autoplay
  useEffect(() => {
    if (!emblaApi) return
    const interval = setInterval(() => {
      emblaApi.scrollNext()
    }, 4500)
    return () => clearInterval(interval)
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  )

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  return (
    <section className="bg-[#f7f4ef] py-24">
      <div className="section-padding mx-auto max-w-7xl">
        <AnimatedSection className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">
            Our Portfolio
          </span>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-black sm:text-4xl">
            Featured Projects
          </h2>
          <p className="mt-5 text-sm leading-7 text-neutral-600">
            Explore some of our most impactful surveying projects across Nigeria,
            from boundary surveys to large-scale topographic mapping.
          </p>
          </div>
          <Link to="/projects" className="btn-outline self-start md:self-auto">
            View All Projects
            <ArrowRight className="w-4 h-4" />
          </Link>
        </AnimatedSection>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
                >
                  <Link to={`/projects/${project.id}`}>
                    <div className="group relative cursor-pointer overflow-hidden bg-black">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={project.image_url}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white px-3 py-1 text-xs font-semibold text-black">
                          {project.category}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="mb-2 text-xl font-semibold text-white">
                          {project.title}
                        </h3>
                        <div className="flex items-center gap-1 text-neutral-300 text-sm">
                          <MapPin className="w-4 h-4" />
                          {project.location}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 lg:-translate-x-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-black shadow-lg backdrop-blur transition-all hover:bg-accent hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous project"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 lg:translate-x-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-black shadow-lg backdrop-blur transition-all hover:bg-accent hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next project"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Carousel Dots */}
        {!isLoading && projects.length === 0 && <p className="text-center text-neutral-500">Projects will appear here soon.</p>}
        <div className="flex justify-center gap-2 mt-8">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                selectedIndex === index
                  ? 'bg-accent w-8'
                  : 'bg-neutral-300 hover:bg-neutral-400'
              }`}
            />
          ))}
        </div>

      </div>
    </section>
  )
}
